import { DbStatus, Horse, HorseActionSuccessTypeEnum } from '@sleipai/sleip-models';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import * as HorsesActions from '../actions/horses.actions';

export const horsesFeatureKey = 'horses';

export interface State extends EntityState<Partial<Horse>> {
  totalHorseCount: number;
  resultPerPage: number;
  numberOfPages: number;
  loading: boolean;
  loaded: boolean;
  horseSaved: boolean;
  horseRemoved: boolean;
  error: string | any | null;
  horseActionSuccessType: HorseActionSuccessTypeEnum | null;
  batchStopShareFailedIds: string[] | null;
  batchDeleteFailedIds: string[] | null;
  batchCloneFailedIds: string[] | null;
  hasMoreHorses: boolean;
  lastSearchIds: string[] | null;
}

export function selectHoseId(horse: Partial<Horse>) {
  return horse.id ?? '';
}

export function sortByName(a: Partial<Horse>, b: Partial<Horse>) {
  return a.name?.localeCompare(b.name ?? '') ?? -1;
}

export const adapter: EntityAdapter<Partial<Horse>> = createEntityAdapter<
  Partial<Horse>
>({
  selectId: selectHoseId,
  sortComparer: sortByName,
});

export const initialState: State = adapter.getInitialState({
  totalHorseCount: 0,
  resultPerPage: 0,
  numberOfPages: 0,
  loading: false,
  loaded: false,
  horseSaved: false,
  horseRemoved: false,
  error: null,
  horseActionSuccessType: null,
  batchStopShareFailedIds: null,
  batchDeleteFailedIds: null,
  batchCloneFailedIds: null,
  hasMoreHorses: false,
  lastSearchIds: null,
});

const horseReducer = createReducer(
  initialState,
  on(HorsesActions.loadHorses, (state) => {
    return {
      ...state,
      loaded: false,
      loading: true,
    };
  }),
  on(HorsesActions.loadAdminHorses, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(HorsesActions.loadHorsesByIds, (state) => {
    return {
      ...state,
      loading: true,
      loaded: false,
    };
  }),
  on(HorsesActions.addHorses, (state, { horses }) => {
    return adapter.upsertMany(horses, {
      ...state,
      loaded: true,
      loading: false,
    });
  }),
  on(HorsesActions.addSingleHorse, (state, { horse }) => {
    return adapter.upsertOne(horse, {
      ...state,
      loaded: true,
      loading: false,
    });
  }),
  on(HorsesActions.loadHorsessSuccess, (state) => state),
  on(HorsesActions.loadHorsesFailure, (state, { error }) => {
    return {
      ...state,
      loaded: true,
      loading: false,
      error: error?.message ?? error,
    };
  }),
  on(
    HorsesActions.loadHorsesForInfiniteScrollList,
    (state, { reset = false }) => {
      const clearedState = reset ? adapter.removeAll(state) : state;
      return {
        ...clearedState,
        loaded: false,
        loading: true,
        lastSearchIds: null,
      };
    }
  ),
  on(HorsesActions.loadHorsesFromAlgoliaSidb, (state) => {
    return {
      ...state,
      loaded: false,
      loading: true,
    };
  }),
  on(
    HorsesActions.loadHorsesFromAlgoliaSidbSuccess,
    (state, { data, updateTotalCount = false }) => {
      return adapter.upsertMany(data.horsesList, {
        ...state,
        totalHorseCount: updateTotalCount
          ? data.totalHorseCount
          : state.totalHorseCount,
        resultPerPage: data.resultPerPage,
        numberOfPages: updateTotalCount
          ? data.numberOfPages
          : state.numberOfPages,
        loaded: true,
        loading: false,
        lastSearchIds: (data.page ?? 0) > 0
          ? [...(state.lastSearchIds ?? []), ...data.horsesList.map((h: any) => h.id)]
          : data.horsesList.map((h: any) => h.id),
      });
    }
  ),
  on(
    HorsesActions.loadHorsesForInfiniteScrollListSuccess,
    (state, { horses, hasMore }) => {
      return adapter.upsertMany(horses, {
        ...state,
        hasMoreHorses: hasMore,
        loaded: true,
        loading: false,
      });
    }
  ),
  on(HorsesActions.createHorseWithTags, (state) => {
    return {
      ...state,
      loaded: false,
      loading: true,
      error: null,
      horseSaved: false,
    };
  }),
  on(HorsesActions.updateHorseWithTags, (state) => {
    return {
      ...state,
      loaded: false,
      loading: true,
      error: null,
      horseSaved: false,
    };
  }),
  on(
    HorsesActions.saveHorseWithTagsSuccess,
    (state, { data, horseActionSuccessType }) => {
      return adapter.upsertOne(data, {
        ...state,
        loaded: true,
        loading: false,
        error: null,
        horseSaved: true,
        totalHorseCount: state.totalHorseCount + 1,
        horseActionSuccessType: horseActionSuccessType,
      });
    }
  ),
  on(HorsesActions.saveHorseWithTagsFailure, (state, { error }) => {
    return {
      ...state,
      loaded: true,
      loading: false,
      error: error,
    };
  }),
  on(HorsesActions.resetHorseSavedFlag, (state) => {
    return {
      ...state,
      horseSaved: false,
      horseActionSuccessType: null,
    };
  }),
  on(HorsesActions.removeHorse, (state) => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(HorsesActions.updateHorseName, (state) => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(HorsesActions.cloneHorse, (state) => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(
    HorsesActions.removeHorseSuccess,
    (state, { horseId, horseActionSuccessType }) => {
      return adapter.removeOne(horseId, {
        ...state,
        loading: false,
        horseRemoved: true,
        totalHorseCount: state.totalHorseCount - 1,
        horseActionSuccessType: horseActionSuccessType,
      });
    }
  ),
  on(
    HorsesActions.horseUpdateSuccess,
    (state, { update, horseActionSuccessType }) => {
      return adapter.updateOne(update, {
        ...state,
        loading: false,
        horseActionSuccessType: horseActionSuccessType,
      });
    }
  ),
  on(HorsesActions.cloneHorseSuccess, (state, { horseActionSuccessType }) => {
    return {
      ...state,
      loading: false,
      horseActionSuccessType,
    };
  }),
  on(HorsesActions.horseDetailsActionFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      error: error?.message ?? error,
    };
  }),
  on(HorsesActions.resetRemoveHorseFlag, (state) => {
    return {
      ...state,
      horseRemoved: false,
      horseActionSuccessType: null,
    };
  }),
  on(HorsesActions.batchStopShareHorse, (state) => {
    return {
      ...state,
      loading: true,
      error: null,
    };
  }),
  on(
    HorsesActions.batchStopShareHorseSuccess,
    (state, { failedIds, horseActionSuccessType }) => {
      return {
        ...state,
        loading: false,
        horseActionSuccessType,
        batchStopShareFailedIds: failedIds,
      };
    }
  ),
  on(HorsesActions.batchStopShareHorseFailure, (state, { error }) => {
    return {
      ...state,
      loading: false,
      error: error?.message ?? error,
    };
  }),
  on(HorsesActions.mergeHorses, (state) => {
    return {
      ...state,
      error: null,
    };
  }),
  on(HorsesActions.mergeHorsesSuccess, (state, { horseIdToMerge, horseActionSuccessType }) => {
    return adapter.updateOne(
      { id: horseIdToMerge, changes: { dbStatus: DbStatus.REMOVED } },
      { ...state, horseActionSuccessType }
    );
  }),
  on(HorsesActions.batchDeleteHorses, (state) => ({
    ...state,
    error: null,
  })),
  on(HorsesActions.batchDeleteHorsesSuccess, (state, { ids, failedIds, horseActionSuccessType }) => {
    const successfulIds = ids.filter((id) => !failedIds.includes(id));
    const updates = successfulIds.map((id) => ({ id, changes: { dbStatus: DbStatus.REMOVED } }));
    return adapter.updateMany(updates, {
      ...state,
      horseActionSuccessType,
      batchDeleteFailedIds: failedIds,
    });
  }),
  on(HorsesActions.batchDeleteHorsesFailure, (state, { error }) => ({
    ...state,
    error: error?.message ?? error,
  })),
  on(HorsesActions.batchCloneHorses, (state) => ({ ...state, error: null })),
  on(HorsesActions.batchCloneHorsesSuccess, (state, { failedIds, horseActionSuccessType }) => ({
    ...state,
    horseActionSuccessType,
    batchCloneFailedIds: failedIds,
  })),
  on(HorsesActions.batchCloneHorsesFailure, (state, { error }) => ({
    ...state,
    error: error?.message ?? error,
  }))
);

// get the selectors
export const {
  selectIds: selectHorseIdsAdapter,
  selectEntities: selectHorseEntitiesAdapter,
  selectAll: selectAllHorsesAdapter,
  selectTotal: selectHorsesTotalAdapter,
} = adapter.getSelectors();

export function reducer(state: State | undefined, action: Action) {
  return horseReducer(state, action);
}
